import { ServerGetPayload } from "../../../dto/ServerRequest";
import { ServerGetResponse } from "../../../dto/ServerResponse";
import { API_URL, requestData } from "./utils";

export const LOGS_DATE_TIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

export interface AuditLogsFilterParams extends Pick<ServerGetPayload, "sort-order"> {
  "api-key"?: string;
  network?: string;
  "enterprise-id"?: string;
  "date-from"?: string;
  "date-to"?: string;
  "event-type"?: string;
  tz?: string;
}

export interface TransactionHistoryFilterParams
  extends Pick<ServerGetPayload, "sort-order"> {
  "api-key"?: string;
  "enterprise-id"?: string;
  "date-from"?: string;
  "date-to"?: string;
  tz?: string;
}

export interface GetAuditLogsParams
  extends AuditLogsFilterParams,
    ServerGetPayload {}

export interface GetTransactionHistoryParams
  extends TransactionHistoryFilterParams,
    ServerGetPayload {}

export interface CdrAuditEvent {
  _links: {
    self: {
      href: string;
    };
  };
  cdr: {
    trx_id: string;
    date_received: string;
    product: string;
    sub_product: string;
    enterprise_id: string;
    enterprise_name: string;
    entity_id: string;
    entity_name: string;
    reseller_id: string;
    event_type: string;
    event_code: string;
    event_label: string;
    event_short_description: string;
    executed_by_user_id: string;
    executed_by_user_name: string;
    executed_by_user_email: string;
    executed_by_admin_user_id: string;
    executed_by_admin_user_name: string;
    executed_by_admin_user_email: string;
    executed_by_api_key: string;
    user_id: string;
    admin_user_id: string;
    api_key: string;
    details: string;
  };
}

export interface CdrGetAuditEventResponse {
  total_found: number;
  cdrs: CdrAuditEvent[];
}

interface QueryResponse {
  page_size: number;
  page: number;
  _embedded: {
    total_found: number;
    [key: string]: any;
  };
}
export default class CdrModule {
  getAuditEvents(
    query: GetAuditLogsParams,
    link: string = "/search/cdr/audit/event",
  ) {
    return requestData<
      ServerGetResponse<CdrGetAuditEventResponse>,
      GetAuditLogsParams
    >("GET", link, query);
  }

  getAuditLogsCsv(
    query: AuditLogsFilterParams,
    link: string = "/search/cdr/audit/extract",
  ) {
    return requestData("GET", link, query);
  }

  getEventTypes(link: string = "/search/cdr/audit/event_type") {
    return requestData<{
      all_valid_event_types: {
        event_type: string;
        description: string;
        description_JP: string;
      }[];
    }>("GET", link);
  }

  getTransactionReport(query, link: string = "/cdr/report/transaction-report") {
    if (query["dlr-error-code"] === "-" || query["dlr-error-code"] === " ") {
      delete query["dlr-error-code"];
      query["status"] = "INPROGRESS";
    }
    if (query["client-reference"] === "-" || query["client-reference"] === " ") return;
    return requestData("GET", link, query);
  }

  getTransactionReportCsvURL(
    query,
    link: string = "/download/cdr/report/transaction-report/csv",
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  generateMTTransactionReport(
    query,
    link: string = "/generate/cdr/report/mt/transaction-report",
  ) {
    return requestData("GET", link, query);
  }

  generateItemizationReport(
    query,
    link: string = "/generate/cdr/report/itemization-report/csv",
  ) {
    return requestData("GET", link, query);
  }

  generateMOTransactionReport(
    query,
    link: string = "/generate/cdr/report/mo/transaction-report",
  ) {
    return requestData("GET", link, query);
  }

  generateSummaryReport(
    query,
    link: string = "/generate/cdr/bulk/summary/campaign-history",
  ) {
    return requestData("GET", link, query);
  }

  generateConsolidatedReport(
    query,
    link: string = "/generate/cdr/bulk/consolidated/campaign-history",
  ) {
    return requestData("GET", link, query);
  }

  generateClickThruReport(
    query,
    link: string = "/generate/cdr/bulk/campaign-click-thru",
  ) {
    return requestData("GET", link, query);
  }

  asyncReportRequest(query, link: string = "/cdr/report/async-report-request") {
    return requestData("GET", link, query);
  }

  getGeneratedReports(
    query,
    link: string = "/cdr/report/query/async-report-requests",
  ) {
    return requestData("GET", link, query);
  }
  //doesn't work
  deleteGeneratedReport(item_id) {
    return requestData("DELETE", `/cdr/report/async-report-requests/${item_id}`);
  }

  getMOTransactionReport(query, link: string = "/cdr/report/transaction-report/mo") {
    return requestData("GET", link, query);
  }

  getMOTransactionReportCsvURL(
    query,
    link: string = "/download/cdr/report/transaction-report/mo/csv",
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  getCampaignTransactionReport(
    query,
    link: string = "/cdr/report/campaign-transaction-report",
  ) {
    return requestData("GET", link, query);
  }

  getCampaignTransactionReportCsvURL(
    query,
    link: string = "/download/cdr/report/campaign-transaction-report/csv",
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  async getConfirmAPITransactionReport(
    query,
    link: string = "/cdr/report/twofa-transaction-report",
  ) {
    return await requestData("GET", link, query);
  }

  getConfirmAPITransactionReportCsvURL(
    query,
    link: string = "/download/cdr/report/confirm-api-transaction-report/csv",
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }
  //to be honest, this is a completely USELESS check. This is needed on backend.
  getDownloadPermission(query = {}, link: string = "/user/download-permission") {
    return requestData("GET", link, query);
  }

  getItemizationReport(query, link: string = "/cdr/report/itemization-report") {
    return requestData("GET", link, query);
  }

  getItemizationReportCsvURL(
    query,
    link: string = "/download/cdr/report/itemization-report/csv",
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  getAdminUsersURL(query, link: string = "/download/csv/admin-users") {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  getOTTMTTransactionReport(
    query,
    link: string = "/cdr/report/ott/transaction-history/mt",
  ) {
    return requestData("GET", link, query);
  }

  generateOTTMTTransactionReport(
    query,
    link: string = "/generate/cdr/report/ott/transaction-history/mt",
  ) {
    return requestData("GET", link, query);
  }

  getOTTMOTransactionReport(
    query,
    link: string = "/cdr/report/ott/transaction-history/mo",
  ) {
    return requestData("GET", link, query);
  }

  generateOTTMOTransactionReport(
    query,
    link: string = "/generate/cdr/report/ott/transaction-history/mo",
  ) {
    return requestData("GET", link, query);
  }
}
